import {
  type CSSProperties,
  Fragment,
  type MouseEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  colors,
  easingTokens,
  radius,
  shadows,
  transitionTokens,
} from '@ardoq/design-tokens';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { Header4, InlineText, Text } from '@ardoq/typography';
import {
  ButtonSize,
  IconButton,
  PrimaryButton,
  TertiaryButton,
} from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  computePopoverPosition,
  type PopoverPosition,
} from './popoverPlacement';
import {
  type CardPrimaryAction,
  getCardPrimaryActionAndHasBack,
} from './getCardPrimaryActionAndHasBack';
import type { Rect, Size, TourBody, TourPlacement } from './types';
import type { TourFeedbackSentiment } from './tourTracking';
import { TourFeedback } from './TourFeedback';
import { clamp } from 'lodash';
import { NewBadge } from '@ardoq/status-ui';
import { ArdoqLoaderComponent, LoaderColor, LoaderSize } from '@ardoq/loader';

/* UI Components are only placeholders until we get some real designs*/

const CARD_WIDTH = 320;
const ARROW_SIZE = 10;

// Entrance animations: a subtle fade + rise/scale so the card eases into place
// (matching the spotlight's glide) instead of teleporting between steps. The
// centered variant composes the centering transform so it doesn't get clobbered.
const anchoredIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const centeredIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const cardSurface = css`
  position: fixed;
  /* The top-layer host is pointer-events: none so anchored steps can let clicks
     through to the page; the card's own controls have to opt back in. */
  pointer-events: auto;
  width: ${CARD_WIDTH}px;
  background: ${colors.surfaceDefault};
  border-radius: ${radius.medium};
  box-shadow: ${shadows.shadowL};
  /* No overflow:hidden — it would clip AnchoredCard's pointer arrow, which
     renders just outside the card edge. border-radius still rounds the card. */
`;

const CenteredCard = styled.div`
  ${cardSurface};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: ${centeredIn} ${transitionTokens.large} ${easingTokens.cubicOut}
    both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const AnchoredCard = styled.div`
  ${cardSurface};
  animation: ${anchoredIn} ${transitionTokens.large} ${easingTokens.cubicOut}
    both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ProgressBarContainer = styled.div`
  flex: 1;
  height: 8px;
  border-radius: ${radius.medium};
  background: ${colors.brand100};
`;

const ProgressBar = styled.div<{ $totalSteps: number; $currentStep: number }>`
  height: 100%;
  width: ${props => (props.$currentStep / props.$totalSteps) * 100}%;
  border-radius: inherit;
  background: ${colors.brand700};
`;

// Step copy carries the line breaks of the source it was written from — a
// tooltip's "Tip:" line is its own paragraph — so keep them rather than
// collapsing them into one run of text.
const BodyText = styled(Text)`
  white-space: pre-line;
`;

const Arrow = styled.div`
  position: absolute;
  width: ${ARROW_SIZE}px;
  height: ${ARROW_SIZE}px;
  border-radius: 2px 0 0 0;
  background: ${colors.surfaceDefault};
  transform: rotate(45deg);
`;

const getArrowStyle = (
  rect: Rect,
  position: PopoverPosition,
  size: Size
): CSSProperties => {
  const half = ARROW_SIZE / 2;
  if (position.placement === 'bottom' || position.placement === 'top') {
    const targetCenterX = rect.left + rect.width / 2;
    const left = clamp(
      targetCenterX - position.left - half,
      12,
      size.width - 12 - ARROW_SIZE
    );
    return position.placement === 'bottom'
      ? { top: -half, left }
      : { bottom: -half, left };
  }
  const targetCenterY = rect.top + rect.height / 2;
  const top = clamp(
    targetCenterY - position.top - half,
    12,
    size.height - 12 - ARROW_SIZE
  );
  return position.placement === 'right'
    ? { left: -half, top }
    : { right: -half, top };
};

type CardLayout = { position: PopoverPosition; size: Size };

type TourCardBaseProps = {
  title?: string;
  /**
   * Accessible name of the tour dialog. Defaults to a generic product-tour
   * name; a tour scoped to one surface can name that surface instead.
   */
  dialogLabel?: string;
  i18nLocale: I18nLocale;
  body: TourBody;
  stepIndex: number;
  totalSteps: number;
  isForwardStepOnly?: boolean;
  shouldTakeFocus: boolean;
  onBack: VoidFunction;
  onNext: VoidFunction;
  onComplete: VoidFunction;
  onDismiss: VoidFunction;
  feedbackSentiment: TourFeedbackSentiment | null;
  onFeedback: (sentiment: TourFeedbackSentiment) => void;
};

type PrimaryButtonLabelArgs = {
  primaryAction: CardPrimaryAction;
  /**
   * The centered first step of a tour that opens on a modal rather than on a
   * content step: it invites the user in rather than advancing.
   */
  isIntroModal: boolean;
  i18nLocale: I18nLocale;
};

const getPrimaryButtonLabel = ({
  primaryAction,
  isIntroModal,
  i18nLocale,
}: PrimaryButtonLabelArgs): string => {
  if (primaryAction === 'done') {
    return t(i18nLocale, 'productTours:done', 'Done');
  }
  if (isIntroModal) {
    return t(i18nLocale, 'productTours:startTour', 'Start tour');
  }
  return t(i18nLocale, 'productTours:next', 'Next');
};

type TourCardContentProps = TourCardBaseProps & {
  /**
   * Surfaces as `data-tour-anchored` so tests (and debugging) can tell a
   * genuinely anchored step from one that degraded to the centered fallback
   * because its anchor never resolved.
   */
  isAnchored: boolean;
  isIntroModal: boolean;
  isResolving: boolean;
};

const TourCardContent = ({
  isAnchored,
  isIntroModal,
  isResolving,
  shouldTakeFocus,
  title,
  dialogLabel,
  body,
  stepIndex,
  totalSteps,
  isForwardStepOnly,
  onBack,
  onNext,
  onComplete,
  onDismiss,
  feedbackSentiment,
  onFeedback,
  i18nLocale,
}: TourCardContentProps) => {
  const { hasBack, primaryAction } = getCardPrimaryActionAndHasBack({
    stepIndex,
    totalSteps,
    isForwardStepOnly: Boolean(isForwardStepOnly),
  });
  const primaryActionRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const bodyId = useId();

  useEffect(() => {
    if (!shouldTakeFocus) return;
    primaryActionRef.current?.focus({ preventScroll: true });
  }, [stepIndex, shouldTakeFocus]);

  const handleFeedback = (sentiment: TourFeedbackSentiment): void => {
    primaryActionRef.current?.focus({ preventScroll: true });
    onFeedback(sentiment);
  };

  return (
    <div
      role="dialog"
      aria-label={
        dialogLabel ??
        t(i18nLocale, 'productTours:cardAriaLabel', 'Product tour')
      }
      aria-describedby={title ? `${titleId} ${bodyId}` : bodyId}
      data-tour-anchored={String(isAnchored)}
    >
      <Box padding="medium">
        <Stack gap="small">
          <FlexBox
            width="full"
            justify="space-between"
            align="center"
            gap="medium"
          >
            {isIntroModal && <NewBadge i18nLocale={i18nLocale} />}
            {!isIntroModal && (
              <FlexBox
                flex={1}
                align="center"
                justify="space-between"
                gap="medium"
              >
                <ProgressBarContainer>
                  <ProgressBar
                    $totalSteps={totalSteps}
                    $currentStep={stepIndex + 1}
                  />
                </ProgressBarContainer>
              </FlexBox>
            )}
            <IconButton
              iconName={IconName.CLOSE}
              label={t(i18nLocale, 'productTours:closeTour', 'Close tour')}
              buttonSize={ButtonSize.SMALL}
              onClick={onDismiss}
            />
          </FlexBox>
          <FlexBox flexDirection="column" gap="small" paddingBottom="small">
            {title && <Header4 id={titleId}>{title}</Header4>}
            <BodyText
              id={bodyId}
              variant="text2"
              color="textSubtle"
              aria-live="polite"
            >
              {typeof body === 'string'
                ? body
                : body.map((segment, index) =>
                    typeof segment === 'string' ? (
                      <Fragment key={index}>{segment}</Fragment>
                    ) : (
                      <InlineText
                        key={index}
                        variant="text2Bold"
                        color="textDefault"
                      >
                        {segment.bold}
                      </InlineText>
                    )
                  )}
            </BodyText>
          </FlexBox>
          {primaryAction === 'done' && (
            <Box
              borderWidthTop={1}
              borderColor="borderSubtle"
              paddingTop="small"
            >
              <TourFeedback
                sentiment={feedbackSentiment}
                onFeedback={handleFeedback}
                i18nLocale={i18nLocale}
              />
            </Box>
          )}

          <FlexBox justify="space-between" align="center">
            <FlexBox align="center" gap="small">
              {!isIntroModal && (
                <Text variant="text2Bold" color="textDefault">
                  {t(
                    i18nLocale,
                    'productTours:stepPosition',
                    { current: stepIndex + 1, total: totalSteps },
                    '{{current}} of {{total}}'
                  )}
                </Text>
              )}
              {isResolving && (
                <ArdoqLoaderComponent
                  loaderType="spinner"
                  size={LoaderSize.SMALL}
                  loaderColor={LoaderColor.GRAY}
                  loaderText={null}
                  ariaLabel={t(
                    i18nLocale,
                    'productTours:stepLoading',
                    'Loading this step'
                  )}
                />
              )}
            </FlexBox>
            <FlexBox
              gap="small"
              align="center"
              width={isIntroModal ? 'full' : 'fit-content'}
            >
              {hasBack && (
                <TertiaryButton buttonSize={ButtonSize.SMALL} onClick={onBack}>
                  {t(i18nLocale, 'productTours:back', 'Back')}
                </TertiaryButton>
              )}
              <PrimaryButton
                ref={primaryActionRef}
                buttonSize={ButtonSize.SMALL}
                isFlexible={isIntroModal}
                onClick={primaryAction === 'done' ? onComplete : onNext}
              >
                {getPrimaryButtonLabel({
                  primaryAction,
                  isIntroModal,
                  i18nLocale,
                })}
              </PrimaryButton>
            </FlexBox>
          </FlexBox>
        </Stack>
      </Box>
    </div>
  );
};

export type CenteredTourCardProps = TourCardBaseProps & {
  /**
   * The card opens the tour rather than carrying one of its steps: it invites
   * the user in, where a step shows its place in the walkthrough.
   */
  isIntroModal: boolean;
  /** The step is still looking for its anchor and may yet move onto it. */
  isResolving: boolean;
};

/** Centered modal card: the intro/outro steps and the anchor-failed fallback. */
export const CenteredTourCard = ({
  isIntroModal,
  isResolving,
  ...props
}: CenteredTourCardProps) => (
  // Re-key per step so the entrance animation replays each time.
  <CenteredCard
    key={props.stepIndex}
    onMouseDown={(event: MouseEvent<HTMLDivElement>): void =>
      // Stop click events from bubbling to the underlying page
      event.stopPropagation()
    }
  >
    <TourCardContent
      isAnchored={false}
      isIntroModal={isIntroModal}
      isResolving={isResolving}
      {...props}
    />
  </CenteredCard>
);

export type AnchoredTourCardProps = TourCardBaseProps & {
  rect: Rect;
  placement: TourPlacement;
};

/** Card anchored to a target: positioned against `rect`, with a pointer arrow. */
export const AnchoredTourCard = ({
  rect,
  placement,
  title,
  dialogLabel,
  body,
  stepIndex,
  totalSteps,
  isForwardStepOnly,
  shouldTakeFocus,
  onBack,
  onNext,
  onComplete,
  onDismiss,
  feedbackSentiment,
  onFeedback,
  i18nLocale,
}: AnchoredTourCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<CardLayout | null>(null);

  useLayoutEffect(() => {
    if (!cardRef.current) {
      setLayout(null);
      return;
    }
    const { width, height } = cardRef.current.getBoundingClientRect();
    const size: Size = { width, height };
    setLayout({
      position: computePopoverPosition(rect, size, placement),
      size,
    });
    // `stepIndex` is included so the card re-measures (height changes) per step.
  }, [rect, placement, stepIndex]);

  const style: CSSProperties = layout
    ? { top: layout.position.top, left: layout.position.left }
    : { top: 0, left: 0, visibility: 'hidden' };

  return (
    <AnchoredCard
      // Re-key per step so the entrance animation replays each time.
      key={stepIndex}
      ref={cardRef}
      style={style}
      onMouseDown={(event: MouseEvent<HTMLDivElement>): void =>
        // Stop click events from bubbling to the underlying page
        event.stopPropagation()
      }
    >
      {layout && (
        <Arrow style={getArrowStyle(rect, layout.position, layout.size)} />
      )}
      <TourCardContent
        isAnchored
        isIntroModal={false}
        isResolving={false}
        shouldTakeFocus={shouldTakeFocus && layout !== null}
        title={title}
        dialogLabel={dialogLabel}
        body={body}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        isForwardStepOnly={isForwardStepOnly}
        onBack={onBack}
        onNext={onNext}
        onComplete={onComplete}
        onDismiss={onDismiss}
        feedbackSentiment={feedbackSentiment}
        onFeedback={onFeedback}
        i18nLocale={i18nLocale}
      />
    </AnchoredCard>
  );
};
